import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "../../hooks/use-toast";
import EnquiryThread, { StatusBadge } from "../../components/EnquiryThread";
import { Loader2, Search, UserPlus, Trash2, PoundSterling, MessageSquare, X, Copy, Link2, Inbox } from "lucide-react";

const dealLabel = (d) => {
  if (!d) return "No pay deal set";
  if (d.deal_type === "percent") return `${d.value}% of sales${d.min_fee ? `, min £${d.min_fee}` : ""}`;
  if (d.deal_type === "per_ticket") return `£${d.value}/ticket${d.min_fee ? `, min £${d.min_fee}` : ""}`;
  return `£${d.value} flat fee`;
};

export default function RosterTab() {
  const [section, setSection] = useState("roster");
  return (
    <div>
      <div className="flex gap-2 mb-5">
        <button onClick={() => setSection("roster")} data-testid="roster-section-roster" className={`rounded-full px-4 py-2 text-sm font-medium ${section === "roster" ? "bg-[#2ec9c9] text-white" : "bg-white text-[#5a6b80]"}`}>My roster</button>
        <button onClick={() => setSection("inbox")} data-testid="roster-section-inbox" className={`rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${section === "inbox" ? "bg-[#2ec9c9] text-white" : "bg-white text-[#5a6b80]"}`}><Inbox size={15} /> Enquiries</button>
      </div>
      {section === "roster" ? <Roster /> : <Enquiries />}
    </div>
  );
}

function Roster() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [invite, setInvite] = useState(false);
  const [deal, setDeal] = useState(null); // artist for deal modal
  const load = () => api.get("/business/artists").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const search = async () => { if (!q.trim()) { setFound([]); return; } const { data } = await api.get(`/business/artists/search?q=${encodeURIComponent(q)}`); setFound(data); };
  const link = async (aid) => { await api.post("/business/artists/link", { artist_id: aid }); toast({ title: "Artist added to roster" }); setFound([]); setQ(""); load(); };
  const remove = async (aid) => { await api.delete(`/business/artists/${aid}/link`); toast({ title: "Removed from roster" }); load(); };

  if (!list) return <Loader2 className="animate-spin text-[#2ec9c9]" size={28} />;
  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <div className="flex flex-wrap items-center gap-2 justify-between mb-3">
          <h3 className="font-semibold text-[#16243b]">Add an artist</h3>
          <button onClick={() => setInvite(true)} data-testid="invite-artist-btn" className="gp-btn-teal rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1.5"><UserPlus size={15} /> Invite new artist</button>
        </div>
        <div className="flex gap-2 max-w-[440px]">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search existing artists by name…" data-testid="roster-search"
            className="flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
          <button onClick={search} className="gp-btn-outline rounded-xl px-4 py-2.5 text-sm font-medium flex items-center gap-1.5"><Search size={15} /> Search</button>
        </div>
        {found.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="roster-search-results">
            {found.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-xl bg-[#f6f8fb] p-2.5">
                <span className="text-sm font-medium text-[#16243b]">{a.name}{a.city ? ` · ${a.city}` : ""}</span>
                {a.linked ? <span className="text-xs text-[#8a97a8]">Already in roster</span>
                  : <button onClick={() => link(a.id)} data-testid={`link-artist-${a.id}`} className="text-xs font-medium text-[#1fb0b0] border border-[#2ec9c9] rounded-full px-3 py-1 flex items-center gap-1"><Link2 size={12} /> Add</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3" data-testid="roster-list">
        {list.length === 0 ? <p className="text-[#8a97a8] text-sm">No artists in your roster yet. Search above or invite a new artist.</p> : list.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl p-4 shadow-[0_6px_20px_rgba(20,30,55,0.06)]" data-testid={`roster-artist-${a.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#eefafa] overflow-hidden shrink-0 flex items-center justify-center">
                {a.image_url ? <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" /> : <PoundSterling className="text-[#2ec9c9]" size={18} />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[#16243b] truncate">{a.name}</p>
                <p className="text-xs text-[#8a97a8]">{a.claimed ? "Manages own profile" : a.invited ? "Invited — awaiting sign-up" : "Roster profile"}</p>
                {a.base_fee != null && <p className="text-xs text-[#5a6b80] mt-0.5">Expected fee: £{Number(a.base_fee).toFixed(0)}</p>}
                <p className="text-xs mt-1 inline-block rounded-full bg-[#f0fbfb] text-[#1fb0b0] px-2 py-0.5 font-medium" data-testid={`deal-label-${a.id}`}>{dealLabel(a.deal)}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setDeal(a)} data-testid={`set-deal-${a.id}`} className="flex-1 gp-btn-outline rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1"><PoundSterling size={13} /> Pay deal</button>
              <button onClick={() => remove(a.id)} data-testid={`remove-artist-${a.id}`} className="px-3 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      {invite && <InviteModal onClose={() => setInvite(false)} onDone={load} />}
      {deal && <DealModal artist={deal} onClose={() => setDeal(null)} onDone={load} />}
    </div>
  );
}

function InviteModal({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", contact_email: "", base_fee: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { claim_url, has_email }
  const submit = async () => {
    if (!f.name.trim()) { toast({ title: "Enter a name", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/business/artists/invite", { name: f.name, contact_email: f.contact_email || null, base_fee: f.base_fee === "" ? null : Number(f.base_fee) });
      const claim_url = `${window.location.origin}${data.claim_path}`;
      setResult({ claim_url, has_email: data.has_email });
      setF({ name: "", contact_email: "", base_fee: "" });
      onDone();
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard.writeText(result.claim_url).then(() => toast({ title: "Invite link copied" }));
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="invite-modal">
      <div className="bg-white rounded-2xl w-full max-w-[460px] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-[#16243b]">Invite an artist</h3><button onClick={onClose}><X className="text-[#5a6b80]" /></button></div>
        {result ? (
          <div className="space-y-3" data-testid="invite-result">
            <p className="text-sm text-[#5a6b80]">Artist added to your roster. Share this link so they can set a password and manage their own profile{result.has_email ? " (we've also emailed it, if email is enabled)" : ""}:</p>
            <div className="flex gap-2">
              <input readOnly value={result.claim_url} onFocus={(e) => e.target.select()} data-testid="invite-claim-link" className="flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-xs outline-none" />
              <button onClick={copy} data-testid="invite-copy" className="gp-btn-teal rounded-xl px-4 text-sm font-medium flex items-center gap-1.5"><Copy size={14} /> Copy</button>
            </div>
            <button onClick={onClose} className="w-full gp-btn-outline rounded-xl py-2.5 font-medium mt-2">Done</button>
          </div>
        ) : (
          <div className="space-y-3">
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Artist / act name" data-testid="invite-name" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
            <input type="email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} placeholder="Contact email (so they can claim their profile)" data-testid="invite-email" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
            <input type="number" value={f.base_fee} onChange={(e) => setF({ ...f, base_fee: e.target.value })} placeholder="Expected fee £ (optional)" data-testid="invite-fee" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
            <button onClick={submit} disabled={busy} data-testid="invite-submit" className="w-full gp-btn-teal rounded-xl py-2.5 font-medium disabled:opacity-60">{busy ? "Creating…" : "Create & get invite link"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function DealModal({ artist, onClose, onDone }) {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(""); // "" = default deal
  const [f, setF] = useState({ deal_type: "percent", value: "", min_fee: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [payout, setPayout] = useState(null);
  useEffect(() => {
    api.get("/business/products").then(({ data }) => setEvents(Array.isArray(data) ? data : (data.products || data.events || [])));
    api.get(`/business/artists/${artist.id}/deals`).then(({ data }) => {
      const def = data.find((d) => !d.event_id);
      if (def) setF({ deal_type: def.deal_type, value: String(def.value), min_fee: String(def.min_fee || ""), note: def.note || "" });
    });
  }, [artist.id]);
  const loadForEvent = (eid) => {
    setEventId(eid);
    api.get(`/business/artists/${artist.id}/deals`).then(({ data }) => {
      const match = data.find((d) => (d.event_id || "") === eid);
      if (match) setF({ deal_type: match.deal_type, value: String(match.value), min_fee: String(match.min_fee || ""), note: match.note || "" });
    });
    setPayout(null);
  };
  const save = async () => {
    if (f.value === "") { toast({ title: "Enter a value", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.put(`/business/artists/${artist.id}/deal`, { deal_type: f.deal_type, value: Number(f.value), min_fee: f.min_fee === "" ? 0 : Number(f.min_fee), note: f.note, event_id: eventId || null });
      toast({ title: eventId ? "Per-gig deal saved" : "Default deal saved" });
      onDone();
      if (!eventId) onClose();
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  const calc = async () => {
    if (!eventId) { toast({ title: "Pick an event first" }); return; }
    const { data } = await api.get(`/business/artists/${artist.id}/payout?event_id=${eventId}`);
    setPayout(data);
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="deal-modal">
      <div className="bg-white rounded-2xl w-full max-w-[480px] p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-[#16243b]">Pay deal · {artist.name}</h3><button onClick={onClose}><X className="text-[#5a6b80]" /></button></div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-[#16243b] uppercase tracking-wide mb-1.5">Applies to</label>
            <select value={eventId} onChange={(e) => loadForEvent(e.target.value)} data-testid="deal-event" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9] bg-white">
              <option value="">Default rate (all gigs)</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
            </select>
            <p className="text-[11px] text-[#8a97a8] mt-1">Pick an event to set a one-off rate that overrides the default for that gig.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#16243b] uppercase tracking-wide mb-1.5">Deal type</label>
              <select value={f.deal_type} onChange={(e) => setF({ ...f, deal_type: e.target.value })} data-testid="deal-type" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9] bg-white">
                <option value="percent">% of ticket sales</option>
                <option value="per_ticket">£ per ticket</option>
                <option value="flat">Flat fee £</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#16243b] uppercase tracking-wide mb-1.5">{f.deal_type === "percent" ? "Percent" : "Amount £"}</label>
              <input type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="deal-value" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#16243b] uppercase tracking-wide mb-1.5">Minimum fee £ (guarantee)</label>
            <input type="number" value={f.min_fee} onChange={(e) => setF({ ...f, min_fee: e.target.value })} placeholder="0" data-testid="deal-min" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
          </div>
          <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (optional)" data-testid="deal-note" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} data-testid="deal-save" className="flex-1 gp-btn-teal rounded-xl py-2.5 font-medium disabled:opacity-60">{busy ? "Saving…" : "Save deal"}</button>
            {eventId && <button onClick={calc} data-testid="deal-calc" className="gp-btn-outline rounded-xl px-4 py-2.5 text-sm font-medium">Calculate</button>}
          </div>
          {payout && (
            <div className="rounded-xl bg-[#f0fbfb] p-3 text-sm" data-testid="deal-payout">
              <p className="text-[#5a6b80]">{payout.tickets_sold} sold · £{payout.gross.toFixed(2)} gross{payout.used_override ? " · using this gig's rate" : " · using default rate"}</p>
              <p className="font-bold text-[#16a34a] text-lg mt-1">Artist earns £{payout.pay != null ? payout.pay.toFixed(2) : "—"}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Enquiries() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [events, setEvents] = useState([]);
  const load = () => api.get("/business/enquiries").then(({ data }) => { setList(data); setOpen((o) => o ? data.find((e) => e.id === o.id) || null : null); });
  useEffect(() => { load(); api.get("/business/products").then(({ data }) => setEvents((Array.isArray(data) ? data : (data.products || data.events || [])).map((e) => ({ id: e.id, title: e.title })))); }, []);
  const send = async (id, body) => { await api.post(`/business/enquiries/${id}/message`, { body }); await load(); };
  const status = async (id, s) => { await api.post(`/business/enquiries/${id}/status`, { status: s }); await load(); toast({ title: `Marked ${s.replace("_", " ")}` }); };
  const book = async (id, eventId) => { await api.post(`/business/enquiries/${id}/book`, { event_id: eventId }); await load(); toast({ title: eventId ? "Booked to event" : "Booking unlinked" }); };
  if (!list) return <Loader2 className="animate-spin text-[#2ec9c9]" size={28} />;
  if (list.length === 0) return <div className="bg-white rounded-2xl p-8 text-center text-[#8a97a8]" data-testid="roster-inbox-empty">No enquiries yet. Artists can reach out to you, and you'll see the conversation here.</div>;
  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-4">
      <div className="space-y-2">
        {list.map((e) => (
          <button key={e.id} onClick={() => setOpen(e)} data-testid={`biz-enquiry-${e.id}`}
            className={`w-full text-left bg-white rounded-xl p-3 border transition-colors ${open?.id === e.id ? "border-[#2ec9c9]" : "border-transparent hover:border-gray-200"}`}>
            <div className="flex items-center justify-between gap-2"><span className="font-medium text-[#16243b] text-sm truncate">{e.artist_name}</span><StatusBadge status={e.status} /></div>
            <p className="text-xs text-[#8a97a8] truncate mt-0.5">{e.event_title || e.subject}</p>
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        {open ? <EnquiryThread enquiry={open} role="business" onSend={send} onStatus={status} events={events} onBook={book} /> : <p className="text-[#8a97a8] text-center py-10">Pick an enquiry to view the conversation.</p>}
      </div>
    </div>
  );
}
